/**
 * Reusable parameter selector with autocomplete from manual-entry-parameters API.
 * Allows selecting from predefined parameters or typing a custom name ("Other").
 * Used in task creation and completion forms for testing tasks to ensure
 * parameter names stay consistent with the parameter catalog.
 */
import { useMemo } from 'react';
import {
  Combobox,
  InputBase,
  useCombobox,
  Loader,
} from '@mantine/core';
import { useManualEntryParameters } from '@/api/measurements';

interface ParameterSelectProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
}

export function ParameterSelect({
  value,
  onChange,
  label = 'Parameter',
  placeholder = 'Select or type parameter name',
  required,
  error,
}: ParameterSelectProps) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const { data: parametersData, isLoading } = useManualEntryParameters();

  const parameterOptions = useMemo(() => {
    if (!parametersData?.parameters) return [];
    return parametersData.parameters.map((p) => ({
      value: p.display_name,
      label: p.unit ? `${p.display_name} (${p.unit})` : p.display_name,
    }));
  }, [parametersData]);

  const filteredOptions = useMemo(() => {
    const search = value.toLowerCase().trim();
    if (!search) return parameterOptions;
    return parameterOptions.filter((opt) =>
      opt.label.toLowerCase().includes(search)
    );
  }, [parameterOptions, value]);

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(val) => {
        onChange(val);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <InputBase
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          value={value}
          onChange={(e) => {
            onChange(e.currentTarget.value);
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          rightSection={isLoading ? <Loader size={16} /> : null}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt) => (
              <Combobox.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Combobox.Option>
            ))
          ) : (
            <Combobox.Empty>
              {value.trim()
                ? `"${value}" will be used as entered`
                : 'No parameters found'}
            </Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
